/**
 * React Query hooks for entitlements — master / child model.
 *
 * Queries use `directApiClient` for client-side reads. Mutations call server
 * actions so revalidatePath fires; on success they invalidate the matter-level
 * entitlements keys and the waterfall (everything downstream recomputes).
 */

'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { SMART_CACHE_CONFIG, FRESH_DATA_CONFIG } from '../query-config';
import { directApiClient } from '@/lib/api/direct-client';
import {
  entitlementKeys,
  invalidateAfterEntitlementUpdate
} from '@/lib/query-keys';
import {
  createEntitlementAction,
  deleteEntitlementAction,
  prefillIntestacyAction,
  resolveEntitlementClassAction,
  updateEntitlementAction,
  updateEntitlementClassMembersAction,
  updateRealisedValuesAction
} from './actions';
import type {
  AmountUpdate,
  CreateEntitlementRequest,
  EntitlementClassResponse,
  EntitlementResponse,
  PrefillIntestacyRequest,
  ResolveEntitlementClassRequest,
  UpdateEntitlementClassMembersRequest,
  UpdateEntitlementRequest,
  WaterfallResponse
} from './types';

// =============================================================================
// QUERY HOOKS
// =============================================================================

export function useEntitlements(matterId: number) {
  return useQuery<EntitlementResponse[]>({
    queryKey: entitlementKeys.forMatter(matterId),
    queryFn: async () => {
      return directApiClient.get<EntitlementResponse[]>(
        `/matters/${matterId}/entitlements`
      );
    },
    enabled: !!matterId,
    ...SMART_CACHE_CONFIG
  });
}

export function useEntitlement(matterId: number, entitlementId: number | null) {
  return useQuery<EntitlementResponse>({
    queryKey:
      entitlementId != null
        ? entitlementKeys.detail(matterId, entitlementId)
        : entitlementKeys.details(),
    queryFn: async () => {
      if (entitlementId == null) {
        throw new Error('entitlementId is required');
      }
      return directApiClient.get<EntitlementResponse>(
        `/matters/${matterId}/entitlements/${entitlementId}`
      );
    },
    enabled: !!matterId && entitlementId != null,
    ...SMART_CACHE_CONFIG
  });
}

export function useWaterfall(matterId: number) {
  return useQuery<WaterfallResponse>({
    queryKey: entitlementKeys.waterfall(matterId),
    queryFn: async () => {
      return directApiClient.get<WaterfallResponse>(
        `/matters/${matterId}/entitlements/waterfall`
      );
    },
    enabled: !!matterId,
    // The waterfall drives "Cash available to distribute" on the Payments tab.
    // A collection recorded on another screen (inventory drawer / institution
    // payout) only marks this key stale; under SMART_CACHE_CONFIG
    // (refetchOnMount: false) navigating back to Payments then served the stale
    // cached figure until a hard reload. Use FRESH_DATA_CONFIG so the figure is
    // always refetched on mount — matching its sibling `useDistributions`, which
    // already does this and stayed fresh while this one didn't.
    ...FRESH_DATA_CONFIG
  });
}

// =============================================================================
// CLASS QUERY HOOKS
// =============================================================================

export function useEntitlementClasses(matterId: number) {
  return useQuery<EntitlementClassResponse[]>({
    queryKey: entitlementKeys.classesForMatter(matterId),
    queryFn: async () => {
      return directApiClient.get<EntitlementClassResponse[]>(
        `/matters/${matterId}/entitlement-classes`
      );
    },
    enabled: !!matterId,
    ...SMART_CACHE_CONFIG
  });
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

/**
 * Local alias for the canonical entitlement invalidation helper.
 * Routes through `lib/services/query-dependencies.ts` (entity: `entitlement`),
 * which is the single source of truth — see `lib/query-keys/CLAUDE.md`.
 */
async function invalidateAll(
  queryClient: ReturnType<typeof useQueryClient>,
  matterId: number
) {
  await invalidateAfterEntitlementUpdate(queryClient, matterId);
}

export function useCreateEntitlement(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateEntitlementRequest) => {
      const result = await createEntitlementAction(matterId, data);
      if (!result.success) {
        throw new Error(result.error || 'Failed to create entitlement');
      }
      return result.data!;
    },
    onSuccess: async () => {
      await invalidateAll(queryClient, matterId);
      toast.success('Entitlement created');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to create entitlement');
    }
  });
}

export function useUpdateEntitlement(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entitlementId,
      data
    }: {
      entitlementId: number;
      data: UpdateEntitlementRequest;
    }) => {
      const result = await updateEntitlementAction(
        matterId,
        entitlementId,
        data
      );
      if (!result.success) {
        throw new Error(result.error || 'Failed to update entitlement');
      }
      return result.data!;
    },
    onSuccess: async () => {
      await invalidateAll(queryClient, matterId);
      toast.success('Entitlement updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update entitlement');
    }
  });
}

export function useDeleteEntitlement(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (entitlementId: number) => {
      const result = await deleteEntitlementAction(matterId, entitlementId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete entitlement');
      }
    },
    onSuccess: async () => {
      await invalidateAll(queryClient, matterId);
      toast.success('Entitlement deleted');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to delete entitlement');
    }
  });
}

export function usePrefillIntestacy(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: PrefillIntestacyRequest = {}) => {
      const result = await prefillIntestacyAction(matterId, data);
      if (!result.success) {
        throw new Error(result.error || 'Failed to prefill intestacy');
      }
      return result.data!;
    },
    onSuccess: async () => {
      await invalidateAll(queryClient, matterId);
      toast.success('Intestacy defaults applied');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to prefill intestacy');
    }
  });
}

export function useUpdateRealisedValues(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updates: AmountUpdate[]) => {
      const result = await updateRealisedValuesAction(matterId, updates);
      if (!result.success) {
        throw new Error(result.error || 'Failed to update realised values');
      }
      return result.data!;
    },
    onSuccess: async () => {
      await invalidateAll(queryClient, matterId);
      toast.success('Amounts updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update amounts');
    }
  });
}

// =============================================================================
// CLASS MUTATION HOOKS
// =============================================================================

export function useResolveEntitlementClass(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entitlementClassId,
      data
    }: {
      entitlementClassId: number;
      data: ResolveEntitlementClassRequest;
    }) => {
      const result = await resolveEntitlementClassAction(
        matterId,
        entitlementClassId,
        data
      );
      if (!result.success) {
        throw new Error(result.error || 'Could not save who is in the group');
      }
      return result.data!;
    },
    onSuccess: async () => {
      await invalidateAll(queryClient, matterId);
      toast.success('Saved who is in the group');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not save who is in the group');
    }
  });
}

export function useUpdateEntitlementClassMembers(matterId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entitlementClassId,
      data
    }: {
      entitlementClassId: number;
      data: UpdateEntitlementClassMembersRequest;
    }) => {
      const result = await updateEntitlementClassMembersAction(
        matterId,
        entitlementClassId,
        data
      );
      if (!result.success) {
        throw new Error(result.error || 'Could not save who is in the group');
      }
      return result.data!;
    },
    onSuccess: async () => {
      await invalidateAll(queryClient, matterId);
      toast.success('Saved who is in the group');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not save who is in the group');
    }
  });
}
